/**
 * Customer data form configuration.
 *
 * Please see further documentation in the `src/types.ts` file.
 */

import { ConfigurableFormConfig, JSONSchema7Object } from "../../../types";
import { isPostCodeRequired } from "../../../utils/countries";
import { get, isNotEmpty } from "../../../utils/helpers";
import * as formElements from "../../shared/forms/formElements";
import formElementsSchema from "../../shared/forms/formElements.json";
import validateSchema from "./customerData.json";
import rimowaFormElementsSchema from "./formElements.json";

export const formConfig: ConfigurableFormConfig = {
  fields: [
    {
      ...formElements.sectionHeader,
      label: "customer.data.sections.personalInformation.title"
    },
    {
      ...formElements.group,
      id: "customer-name",
      props: {
        className: "customer-name"
      },
      fields: [
        {
          ...formElements.fieldTitle,
          id: "honorificPrefix",
          name: "honorificPrefix",
          label: "customer.data.fields.salutation.label",
          errorMessage: "customer.data.fields.honorificPrefix.errors.required",
          stateToProps: {
            required: (value: any, propName?: string, input?: any) =>
              get(input, "pageState.language") !== "ja"
          },
          props: {
            inputProps: {
              sortOptionsAlphabetically: false
            }
          }
        },
        {
          ...formElements.fieldText,
          id: "givenName",
          name: "givenName",
          label: "customer.data.fields.givenName.label",
          props: {
            required: true,
            enterInRoman: true,
            inputProps: {
              maxLength: 35
            }
          },
          errorMessage: "customer.data.fields.givenName.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "familyName",
          name: "familyName",
          label: "customer.data.fields.familyName.label",
          props: {
            required: true,
            enterInRoman: true,
            inputProps: {
              maxLength: 35
            }
          },
          errorMessage: "customer.data.fields.familyName.errors.required"
        }
      ]
    },
    {
      ...formElements.group,
      id: "katakana-fields",
      props: {
        inline: true
      },
      showOnlyIf: (sharedState: any) => {
        const { pageState } = sharedState;
        if (pageState.language === "ja") {
          return true;
        }
        return;
      },
      fields: [
        {
          ...formElements.fieldText,
          id: "katakanaFirstname",
          name: "katakanaFirstname",
          label: "customer.data.fields.katakana.firstname.label",
          props: {
            required: true,
            inputProps: {
              maxLength: 15
            }
          },
          errorMessage:
            "customer.data.fields.katakana.firstname.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "katakanaLastname",
          name: "katakanaLastname",
          label: "customer.data.fields.katakana.lastname.label",
          props: {
            required: true,
            inputProps: {
              maxLength: 15
            }
          },
          errorMessage: "customer.data.fields.katakana.lastname.errors.required"
        }
      ]
    },
    {
      ...formElements.fieldCpf,
      id: "CpfCnpjCode",
      name: "CpfCnpjCode",
      label: "customer.cpffield.label",
      props: {
        inputProps: {
          maxLength: 14,
          minLength: 14
        }
      },
      errorMessage: {
        _: "customer.cpffield.formaterror",
        cpfCustomValidtion: "customer.cpffield.invaliderror"
      },
      showOnlyIf: (sharedState: any) => {
        const appState = sharedState.appState;
        const storeId = appState.storeId;
        // If storeId IS includes in the salesforce metadata features
        return (
          (!("CPF" in appState.config.pilotConfig) ||
            (appState.config.pilotConfig.CPF &&
              appState.config.pilotConfig.CPF.map((store: string) =>
                store.trim()
              ).includes(storeId))) &&
          appState.zone === "br"
        );
      }
    },
    {
      ...formElements.fieldBirthDate,
      id: "birthDate",
      name: "birthDate",
      label: "customer.data.fields.birthDate.label",
      props: {
        inputProps: {
          labels: {
            day: "customer.data.fields.birthDate.options.day.label",
            month: "customer.data.fields.birthDate.options.month.label",
            year: "customer.data.fields.birthDate.options.year.label"
            // yearOptionNotSet: "general.fields.notSet.label"
          },
          minimumAge: 16,
          maximumAge: 101
        }
      },
      stateToProps: {
        "pageState.language": "inputProps.lang",
        "appState.storeCountry": "inputProps.country"
      },
      errorMessage: {
        _: "customer.data.fields.birthDate.errors.required",
        minimumAge:
          "customer.data.fields.birthDate.options.year.errors.underMinimumAge"
      }
    },
    {
      ...formElements.fieldCountry,
      id: "nationality",
      name: "nationality",
      label: "customer.data.fields.nationality.label"
    },
    {
      ...formElements.group,
      id: "thailand-fields",
      showOnlyIf: (sharedState: any) => {
        const { appState } = sharedState;
        const { storeCountry } = appState;
        if (storeCountry === "TH") {
          return true;
        }
        return;
      },
      fields: [
        {
          ...formElements.fieldText,
          id: "PassportNumber",
          name: "PassportNumber",
          label: "customer.data.fields.passport.label",
          errorMessage: "customer.data.fields.passport.errors.invalid"
        },
        {
          ...formElements.fieldTax,
          id: "FiscalId",
          name: "FiscalId",
          label: "customer.data.fields.tax.label",
          errorMessage: "customer.data.fields.tax.errors.invalid"
        }
      ]
    },
    {
      ...formElements.sectionHeader,
      label: "customer.data.sections.contactDetails.title"
    },
    {
      ...formElements.fieldCheckbox,
      id: "eReceipt",
      name: "eReceipt",
      label: "customer.data.fields.eReceipt.label"
    },
    // EMAIL WITHOUT DQE
    {
      ...formElements.fieldEmail,
      id: "email",
      name: "email",
      label: "customer.data.fields.email.label",
      props: {
        inputProps: {
          maxLength: 250
        }
      },
      stateToProps: {
        required: (value: any, propName?: string, input?: any) =>
          get(input, "formState.emailConsent") ||
          get(input, "formState.eReceipt") ||
          get(input, "formState.isRemote")
      },
      errorMessage: {
        _: "customer.data.fields.email.errors.required",
        format: "customer.data.fields.email.errors.invalid"
      },
      showOnlyIf: (sharedState: any) => {
        const appState = sharedState.appState;
        const storeId = appState.storeId;
        // If storeId IS NOT includes in the salesforce metadata features
        return (
          "EMAIL_DQE" in appState.config.pilotConfig &&
          (appState.config.pilotConfig.EMAIL_DQE &&
            !appState.config.pilotConfig.EMAIL_DQE.map((store: string) =>
              store.trim()
            ).includes(storeId))
        );
      }
    },
    // EMAIL WITH DQE
    {
      ...formElements.fieldEmailDqe,
      id: "emailDqe",
      name: "email",
      label: "customer.data.fields.email.label",
      props: {
        inputProps: {
          maxLength: 250,
          valid: ["valid "]
        }
      },
      stateToProps: {
        required: (value: any, propName?: string, input?: any) =>
          get(input, "formState.emailConsent") ||
          get(input, "formState.isRemote")
      },
      errorMessage: {
        _: "customer.data.fields.email.errors.required",
        format: "customer.data.fields.email.errors.invalid"
      },
      showOnlyIf: (sharedState: any) => {
        const appState = sharedState.appState;
        const storeId = appState.storeId;
        // If storeId IS includes in the salesforce metadata features
        return (
          !("EMAIL_DQE" in appState.config.pilotConfig) ||
          (appState.config.pilotConfig.EMAIL_DQE &&
            appState.config.pilotConfig.EMAIL_DQE.map((store: string) =>
              store.trim()
            ).includes(storeId))
        );
      }
    },
    // PHONE WITHOUT DQE
    {
      ...formElements.fieldPhone,
      id: "mobileTelephone",
      name: "mobileTelephone",
      label: "customer.data.fields.mobilePhone.label",
      props: {
        hideLabel: true,
        inputProps: {
          maxLength: 25,
          labels: {
            dialCode: "customer.data.fields.phone.dialCode.label",
            phoneNumber: "customer.data.fields.mobilePhone.label"
          }
        }
      },
      stateToProps: {
        required: (value: any, propName?: string, input?: any) =>
          get(input, "formState.phoneConsent") ||
          get(input, "formState.appsConsent") ||
          get(input, "formState.eReceipt") ||
          get(input, "formState.isRemote"),
        "formState.nationality": "inputProps.country",
        "formState.addressCountry": "inputProps.country"
      },
      showOnlyIf: (sharedState: any) => {
        const appState = sharedState.appState;
        const storeId = appState.storeId;
        // If storeId IS NOT includes in the salesforce metadata features
        return (
          "PHONE_DQE" in appState.config.pilotConfig &&
          (appState.config.pilotConfig.PHONE_DQE &&
            !appState.config.pilotConfig.PHONE_DQE.map((store: string) =>
              store.trim()
            ).includes(storeId))
        );
      }
    },
    // PHONE WITH DQE
    {
      ...formElements.fieldPhoneDqe,
      id: "mobileTelephoneDqe",
      name: "mobileTelephoneDqe",
      label: "customer.data.fields.mobilePhone.label",
      props: {
        hideLabel: true,
        inputProps: {
          searchable: true,
          maxLength: 25,
          labels: {
            dialCode: "customer.data.fields.phone.dialCode.label",
            phoneNumber: "customer.data.fields.mobilePhone.label"
          }
        }
      },
      stateToProps: {
        required: (value: any, propName?: string, input?: any) =>
          get(input, "formState.phoneConsent") ||
          get(input, "formState.appsConsent") ||
          get(input, "formState.eReceipt") ||
          get(input, "formState.isRemote"),
        "formState.nationality": "inputProps.country",
        "formState.addressCountry": "inputProps.country"
      },
      errorMessage: {
        _: "customer.data.fields.phone.errors.required"
      },
      showOnlyIf: (sharedState: any) => {
        const appState = sharedState.appState;
        const storeId = appState.storeId;
        // If storeId IS includes in the salesforce metadata features
        return (
          !("PHONE_DQE" in appState.config.pilotConfig) ||
          (appState.config.pilotConfig.PHONE_DQE &&
            appState.config.pilotConfig.PHONE_DQE.map((store: string) =>
              store.trim()
            ).includes(storeId))
        );
      }
    },
    {
      ...formElements.fieldCountry,
      id: "addressCountry",
      name: "addressCountry",
      label: "customer.data.fields.address.country.label",
      errorMessage: "customer.data.fields.address.country.errors.required",
      stateToProps: {
        "formState.postConsent": "required"
      }
    },
    {
      ...formElements.group,
      id: "customer-address",
      props: {
        className: "customer-address"
      },
      showOnlyIf: {
        match: {
          "formState.addressCountry": isNotEmpty
        }
      },
      fields: [
        // ADDRESS WITHOUT DQE (with google maps autocomplete)
        {
          ...formElements.fieldAutocompleteGoogleMaps,
          id: "streetAddress1Maps",
          name: "streetAddress1",
          label: "customer.data.fields.address.line1.label",
          stateToProps: {
            "formState.addressCountry": "inputProps.country",
            "formState.postConsent": "required"
          },
          props: {
            inputProps: {
              maxLength: 50
            }
          },
          errorMessage: "customer.data.fields.address.line1.errors.required",
          showOnlyIf: (sharedState: any) => {
            const appState = sharedState.appState;
            const formState = sharedState.formState;
            const storeId = appState.storeId;
            // If storeId IS NOT includes in the salesforce metadata features
            return (
              "DQE_ADDRESS" in appState.config.pilotConfig &&
              (appState.config.pilotConfig.DQE_ADDRESS &&
                !appState.config.pilotConfig.DQE_ADDRESS.map((store: string) =>
                  store.trim()
                ).includes(storeId)) &&
              formState.addressCountry !== "JPN"
            );
          }
        },
        // ADDRESS WITH DQE
        {
          ...formElements.fieldAutocompleteDqe,
          id: "streetAddress1MapsDqe",
          name: "streetAddress1",
          label: "customer.data.fields.address.line1.label",
          stateToProps: {
            "formState.addressCountry": "inputProps.country",
            "formState.postConsent": "required"
          },
          props: {
            inputProps: {
              maxLength: 50
            }
          },
          errorMessage: "customer.data.fields.address.line1.errors.required",
          showOnlyIf: (sharedState: any) => {
            const appState = sharedState.appState;
            const formState = sharedState.formState;
            const storeId = appState.storeId;
            // If storeId IS includes in the salesforce metadata features
            return (
              (!("DQE_ADDRESS" in appState.config.pilotConfig) ||
                (appState.config.pilotConfig.DQE_ADDRESS &&
                  appState.config.pilotConfig.DQE_ADDRESS.map((store: string) =>
                    store.trim()
                  ).includes(storeId))) &&
              formState.addressCountry !== "JPN"
            );
          }
        },
        {
          ...formElements.fieldText,
          id: "streetAddress1",
          name: "streetAddress1",
          label: "customer.data.fields.address.line1.label",
          props: {
            inputProps: {
              maxLength: 50
            }
          },
          stateToProps: {
            "formState.postConsent": "required"
          },
          showOnlyIf: {
            has: {
              "formState.addressCountry": "JPN"
            }
          },
          errorMessage: "customer.data.fields.address.line1.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "apartNumber",
          name: "apartNumber",
          label: "customer.data.fields.address.apartNumber.label",
          props: {
            inputProps: {
              maxLength: 50
            }
          },
          showOnlyIf: {
            has: {
              "formState.addressCountry": "BR"
            }
          }
        },
        {
          ...formElements.fieldText,
          id: "streetAddress2",
          name: "streetAddress2",
          label: "customer.data.fields.address.line2.label",
          props: {
            inputProps: {
              maxLength: 50
            }
          }
        },
        {
          ...formElements.fieldText,
          id: "addressLocality",
          name: "addressLocality",
          label: "customer.data.fields.address.city.label",
          props: {
            inputProps: {
              maxLength: 35
            }
          },
          stateToProps: {
            "formState.postConsent": "required"
          },
          errorMessage: "customer.data.fields.address.city.errors.required"
        },
        {
          ...formElements.fieldCountryRegion,
          id: "addressRegion",
          name: "addressRegion",
          label: "customer.data.fields.address.region.label",
          stateToProps: {
            "formState.nationality": "inputProps.country",
            "formState.addressCountry": "inputProps.country"
          }
        },
        {
          ...formElements.fieldText,
          id: "postalCode",
          name: "postalCode",
          label: "customer.data.fields.address.postCode.label",
          props: {
            inputProps: {
              maxLength: 10
            }
          },
          stateToProps: {
            required: (value: any, propName?: string, input?: any) =>
              get(input, "formState.postConsent") &&
              get(input, "formState.addressCountry") &&
              isPostCodeRequired(get(input, "formState.addressCountry"))
          },
          errorMessage: "customer.data.fields.address.postCode.errors.required",
          showOnlyIf: {
            not: {
              has: {
                "formState.addressCountry": "JPN"
              }
            }
          }
        },
        // POSTAL CODE FROM OLD VERSION
        {
          ...formElements.fieldAutocompleteGoogleMaps,
          id: "postalCodeMaps",
          name: "postalCode",
          label: "customer.data.fields.address.postCode.label",
          props: {
            inputProps: {
              maxLength: 10
            }
          },
          stateToProps: {
            "formState.addressCountry": "inputProps.country",
            "formState.postConsent": "required"
          },
          showOnlyIf: (sharedState: any) => {
            const appState = sharedState.appState;
            const formState = sharedState.formState;
            const storeId = appState.storeId;
            // If storeId IS NOT includes in the salesforce metadata features
            return (
              "DQE_ADDRESS" in appState.config.pilotConfig &&
              (appState.config.pilotConfig.DQE_ADDRESS &&
                !appState.config.pilotConfig.DQE_ADDRESS.map((store: string) =>
                  store.trim()
                ).includes(storeId)) &&
              formState.addressCountry === "JPN"
            );
          }
        },
        // POSTAL CODE FROM NEW VERSION
        {
          ...formElements.fieldNumber,
          id: "postalCodeMapsDqe",
          name: "postalCode",
          label: "customer.data.fields.address.postCode.label",
          props: {
            inputProps: {
              maxLength: 10
            }
          },
          stateToProps: {
            "formState.addressCountry": "inputProps.country",
            "formState.postConsent": "required"
          },
          showOnlyIf: (sharedState: any) => {
            const appState = sharedState.appState;
            const formState = sharedState.formState;
            const storeId = appState.storeId;
            // If storeId IS includes in the salesforce metadata features
            return (
              (!("DQE_ADDRESS" in appState.config.pilotConfig) ||
                (appState.config.pilotConfig.DQE_ADDRESS &&
                  appState.config.pilotConfig.DQE_ADDRESS.map((store: string) =>
                    store.trim()
                  ).includes(storeId))) &&
              formState.addressCountry === "JPN"
            );
          }
        }
      ]
    },
    {
      ...formElements.sectionHeader,
      label: "customer.data.sections.communicationMethods.title"
    },
    {
      ...formElements.sectionText,
      label: "customer.data.sections.communicationMethods.intro"
    },
    {
      ...formElements.fieldCheckbox,
      id: "emailConsent",
      name: "emailConsent",
      label: "customer.data.fields.emailConsent.label"
    },
    {
      ...formElements.fieldCheckbox,
      id: "phoneConsent",
      name: "phoneConsent",
      label: "customer.data.fields.phoneConsent.label"
    },
    {
      ...formElements.fieldCheckbox,
      id: "postConsent",
      name: "postConsent",
      label: "customer.data.fields.postConsent.label"
    },
    {
      ...formElements.fieldCheckbox,
      id: "appsConsent",
      name: "appsConsent",
      label: "customer.data.fields.appsConsent.label"
    },
    {
      // EMPTY SECTION FOR SPACE
      ...formElements.sectionText,
      label: " "
    },
    {
      // EMPTY SECTION FOR SPACE
      ...formElements.sectionText,
      label: " "
    },
    // DISPLAY CGI ONLY IF STOREID IS PRESENT IN SALESFORCE METADATA
    {
      ...formElements.group,
      id: "customer-cgi",
      showOnlyIf: (sharedState: any) => {
        const appState = sharedState.appState;
        const storeId = appState.storeId;
        // If storeId IS includes in the salesforce metadata features
        return (
          !("CGI" in appState.config.pilotConfig) ||
          (appState.config.pilotConfig.CGI &&
            appState.config.pilotConfig.CGI.map((store: string) =>
              store.trim()
            ).includes(storeId))
        );
      },
      fields: [
        {
          ...formElements.sectionText,
          id: "cgiConsentTitle",
          label: "customer.data.fields.CGIHeader.label"
        },
        {
          ...formElements.fieldCheckbox,
          label: "customer.data.fields.CGIIagree.label",
          id: "UserDefinedTable7Value",
          name: "UserDefinedTable7Value",
          props: {
            inputProps: {
              IAgreeClassName: "IAgree"
            }
          }
        }
      ]
    }
  ],

  validateSchema: validateSchema as JSONSchema7Object,

  refSchemas: [
    formElementsSchema as JSONSchema7Object,
    rimowaFormElementsSchema as JSONSchema7Object
  ],

  labels: {
    buttonBack: "general.buttons.cancel.label",
    buttonNext: "general.buttons.continue.label",
    buttonSubmit: "customer.data.buttons.next.label"
  }
};

export default formConfig;
